import { reactiveComputed } from '@vueuse/core';
import type { FormInstance, FormItemProps, RuleObject } from 'ant-design-vue/es/form';
import type { FormProps } from 'ant-design-vue';
import { computed, ref } from 'vue';
import type { ComputedRef, UnwrapRef } from 'vue';
import { GROUP_SEP, buildGroupFieldName, getGroupIndex } from './useAntdForm.helpers';

export { useAntdForm };
export type { TField, RecordToSchemas };

function useAntdForm<
  F extends Record<string, TField>,
  S extends GetFormState<F>,
  STF = S,
>(
  schemas: F,
  _options?: {
    transform?: (state: S) => STF
  },
) {
  const formRef = ref<FormInstance>();
  const $form = {
    get clearValidate() {
      return formRef.value?.clearValidate;
    },
    get getFieldsValue() {
      return formRef.value?.getFieldsValue;
    },
    get resetFields() {
      return formRef.value?.resetFields;
    },
    get scrollToField() {
      return formRef.value?.scrollToField;
    },
    get validate() {
      return formRef.value?.validate;
    },
    get validateFields() {
      return formRef.value?.validateFields;
    },
    setField(name: string, schema: TField) {
      (schemas as any)[name] = schema;
    },
    addGroup(groupName: string, groupSchemas: Record<string, TField>) {
      const i = getGroupIndex(groupName, schemas) + 1;

      Object.keys(groupSchemas).forEach((key) => {
        const name = buildGroupFieldName(groupName, i, key);
        (schemas as any)[name] = groupSchemas[key];
      });
    },
    removeGroup(groupName: string, index?: number) {
      const i = index ?? getGroupIndex(groupName, schemas);

      Object.keys(schemas).forEach((key) => {
        if (key.startsWith(groupName + GROUP_SEP + i))
          Reflect.deleteProperty(schemas, key);
      });
    },
  };
  const state = reactiveComputed(() => {
    return Object.fromEntries(Object.entries(schemas).map(([k, v]) => [k, v.value])) as S;
  });
  const props = reactiveComputed<FormProps>(() => {
    return {
      model: state,
      ref: (c: any) => (formRef.value = c),
    };
  });
  const itemProps = reactiveComputed(() => {
    const propsTuple = Object.entries(schemas).map(([key, value]): [string, FormItemProps] => [
      key,
      { name: key, rules: value.rules },
    ]);
    const props = Object.fromEntries(propsTuple) as Record<keyof S, FormItemProps>;

    return props;
  });
  const stateTF = _options?.transform ? computed(() => _options.transform!(state)) : computed(() => state);

  return {
    $form,
    state,
    stateTF: stateTF as ComputedRef<STF>,
    props,
    itemProps,
  };
}

interface TField<V = any> {
  value: V
  rules?: RuleObject[]
}

type RecordToSchemas<T extends Record<string, unknown>> = {
  [K in keyof T]: TField<T[K]>
};

type GetFormState<C extends Record<string, TField>> = {
  [K in keyof C]: UnwrapRef<C[K]['value']>;
};
